// Service pour la gestion des opérations financières (cash flow) utilisant fetch natif
import { RootState, store } from "../store";
import dayjs from "dayjs";

// URL de base de l'API
const API_BASE_URL = `${
  import.meta.env.VITE_API_URL || "http://localhost:5000/api"
}`;

// Import du middleware pour gérer les erreurs 401
import { handleUnauthorizedError } from "./apiMiddleware";

// Types d'opérations financières
export enum CashFlowItemType {
  RECURRENT = "recurrent",
  PONCTUEL = "ponctuel",
}

// Interface pour représenter une opération financière
export interface CashFlowItem {
  _id?: string;
  category: string; // Catégorie de l'opération (ex: loyer, salaires, etc.)
  isRevenue: boolean; // true pour revenu, false pour dépense
  libelle: string; // Description de l'opération
  montantHT: number; // Montant hors taxes
  montantTTC: number; // Montant TTC
  tva: number; // Montant de la TVA
  tauxTVA: 0 | 5.5 | 10 | 20 | undefined; // Taux de TVA appliqué (0, 5.5, 10 ou 20)
  type: CashFlowItemType; // Type d'opération (recurrent ou ponctuel)
  echeance?: string; // Date d'échéance pour les opérations ponctuelles (format YYYY-MM-DD)
  jourDuMois?: number; // Jour du mois pour les prélèvements récurrents (1-31)
  dateDebut: string; // Date de début pour les opérations récurrentes (format YYYY-MM-DD)
  dateFin?: string; // Date de fin pour les opérations récurrentes (null si indéfini) (format YYYY-MM-DD)
  sync?: boolean;
  createdAt?: string; // Date de création de l'enregistrement
  updatedAt?: string; // Date de dernière mise à jour de l'enregistrement
  createdBy?: string; // ID de l'utilisateur qui a créé l'entrée
}

// Interface pour les données du formulaire de création/modification
export interface CashFlowFormData {
  category: string;
  isRevenue: boolean;
  libelle: string;
  montantHT: number;
  montantTTC: number;
  tva: number;
  tauxTVA: 0 | 5.5 | 10 | 20;
  type: CashFlowItemType;
  echeance?: dayjs.Dayjs; // Date pour les opérations ponctuelles
  jourDuMois?: number; // Pour les opérations récurrentes
  dateDebut: dayjs.Dayjs; // Date de début
  dateFin?: dayjs.Dayjs; // Date de fin optionnelle
}

// Interface pour le résultat de prévision du cash flow
export interface CashFlowForecast {
  _id: string; // ID de l'opération d'origine
  date: string; // Date de l'occurrence (YYYY-MM-DD)
  category: string; // Catégorie
  libelle: string; // Description
  montantTTC: number; // Montant TTC
  isRevenue: boolean; // true pour revenu, false pour dépense
  type: CashFlowItemType; // Type d'opération
}

// Interface pour les événements d'historique de cash flow retournés par l'API
export type CashFlowHistoryEventType =
  | "create"
  | "delete"
  | "update"
  | "archive"
  | "echeance_update"
  | "tva_override"
  | "initial_balance_change"
  | "ticket_sync"
  | string;

export interface CashFlowHistoryEventApi {
  _id: string;
  cashFlowId?: string;
  type: CashFlowHistoryEventType;
  title: string;
  details?: string;
  metadata?: any;
  userId?: string;
  createdAt?: string | Date;
  updatedAt?: string | Date;
}

// Interface pour les filtres de recherche
export interface CashFlowFilters {
  category?: string;
  isRevenue?: boolean;
  type?: CashFlowItemType;
  startDate?: string;
  endDate?: string;
  minAmount?: number;
  maxAmount?: number;
}

// Interface pour les données de synchronisation des opérations financières
export interface CashFlowSyncRule {
  _id?: string;
  version?: number;
  name: string;
  enabled?: boolean;
  sourceModel: string;
  category: string;
  isRevenue: boolean;
  titleTemplate: string;
  v2?: {
    amount?: {
      path?: string;
      fallbackPaths?: string[];
    };
    dueDate?: {
      baseDatePaths?: string[];
      baseDatePath?: string;
      transform?: "none" | "end_of_month";
      offsets?: Array<{
        kind: "constant_days" | "field_days";
        days?: number;
        path?: string;
      }>;
    };
    tva?: {
      mode?: "none" | "fixed_rate" | "rate_from_field";
      rate?: 0 | 5.5 | 10 | 20;
      ratePath?: string;
    };
  };
  amountPath: string;
  amountFallbackPaths?: string[];
  amountMin?: number;
  dueDateBasePath?: string;
  dueDateOffsetDays?: number;
  dueDateOffsetFromSupplierPaymentTerms?: boolean;
  supplierIdPath?: string;
  type?: string;
  tauxTVA?: 0 | 5.5 | 10 | 20;
  createdAt?: string;
  updatedAt?: string;
}

export interface CashFlowSyncProposal {
  source: {
    model: string;
    id: string;
    syncRuleId: string | null;
    hash: string;
  };
  category: string;
  isRevenue: boolean;
  libelle: string;
  montantHT: number;
  montantTTC: number;
  tva: number;
  tauxTVA: number;
  type: string;
  echeance: string | Date;
  dateDebut: string | Date;
  sourceDate?: string | Date | null;
  alreadyExists?: boolean;
}

export interface CashFlowSyncPreviewResponse {
  ruleId: string;
  proposals: CashFlowSyncProposal[];
}

export interface CashFlowSyncModelFields {
  stringFields: string[];
  numberFields: string[];
  dateFields: string[];
  booleanFields: string[];
  objectIdFields: string[];
  mixedFields: string[];
  arrayFields: string[];
}

export interface CashFlowSyncMetadataModel {
  modelName: string;
  fields: CashFlowSyncModelFields;
}

export interface CashFlowSyncMetadataResponse {
  models: CashFlowSyncMetadataModel[];
}

export interface CashFlowSyncPreviewExamplePayload {
  sourceModel: string;
  category: string;
  isRevenue: boolean;
  titleTemplate: string;
  amountPath: string;
  amountFallbackPaths?: string[];
  dueDateBasePath?: string;
  dueDateBasePaths?: string[];
  dueDateTransform?: "none" | "end_of_month";
  dueDateOffsetDays?: number;
  tauxTVA?: 0 | 5.5 | 10 | 20;
  tvaMode?: "none" | "fixed_rate" | "rate_from_field";
  tvaRatePath?: string;
  offsetMode?: "constant_days" | "field_days";
  offsetDaysPath?: string;
}

export interface CashFlowSyncPreviewExampleResponse {
  proposal: CashFlowSyncProposal | null;
}

export interface CashFlowSyncApplyResponse {
  created: number;
  skipped: number;
  items: CashFlowItem[];
}

export interface CashFlowSyncProposalsResponse {
  category: string;
  proposals: CashFlowSyncProposal[];
}

export interface CreateCashFlowHistoryPayload {
  cashFlowId?: string;
  type: string;
  title: string;
  details?: string;
  metadata?: any;
}

// Interface pour les filtres de recherche
export interface CashFlowFilters {
  category?: string;
  isRevenue?: boolean;
  type?: CashFlowItemType;
  startDate?: string;
  endDate?: string;
  minAmount?: number;
  maxAmount?: number;
}

// Fonction pour récupérer le token d'authentification depuis le store Redux
const getAuthToken = (): string | null => {
  const state = store.getState() as RootState;
  return state.user.currentUser?.token || null;
};

export const getCashFlowSyncRules = async (): Promise<CashFlowSyncRule[]> => {
  const response = await fetch(`${API_BASE_URL}/finance/cashflow/sync-rules`, {
    headers: getAuthHeaders(),
  });

  if (handleUnauthorizedError(response)) {
    throw new Error("Session expirée. Veuillez vous reconnecter.");
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Erreur HTTP: ${response.status} - ${errorText}`);
  }

  return await response.json();
};

export const getCashFlowSyncProposals = async (
  category: string,
  limit = 50
): Promise<CashFlowSyncProposalsResponse> => {
  const url = new URL(`${API_BASE_URL}/finance/cashflow/sync-proposals`, window.location.origin);
  url.searchParams.set("category", category);
  url.searchParams.set("limit", String(limit));

  const response = await fetch(url.toString(), {
    headers: getAuthHeaders(),
  });

  if (handleUnauthorizedError(response)) {
    throw new Error("Session expirée. Veuillez vous reconnecter.");
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Erreur HTTP: ${response.status} - ${errorText}`);
  }

  return await response.json();
};

export const applyCashFlowSyncProposals = async (
  category: string,
  selectedHashes: string[]
): Promise<CashFlowSyncApplyResponse> => {
  const response = await fetch(`${API_BASE_URL}/finance/cashflow/sync-proposals/apply`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ category, selectedHashes }),
  });

  if (handleUnauthorizedError(response)) {
    throw new Error("Session expirée. Veuillez vous reconnecter.");
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Erreur HTTP: ${response.status} - ${errorText}`);
  }

  return await response.json();
};

export const previewCashFlowSyncExample = async (
  payload: CashFlowSyncPreviewExamplePayload
): Promise<CashFlowSyncPreviewExampleResponse> => {
  const response = await fetch(`${API_BASE_URL}/finance/cashflow/sync/preview-example`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });

  if (handleUnauthorizedError(response)) {
    throw new Error("Session expirée. Veuillez vous reconnecter.");
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Erreur HTTP: ${response.status} - ${errorText}`);
  }

  return await response.json();
};

export const getCashFlowSyncMetadata = async (): Promise<CashFlowSyncMetadataResponse> => {
  const response = await fetch(`${API_BASE_URL}/finance/cashflow/sync-metadata`, {
    headers: getAuthHeaders(),
  });

  if (handleUnauthorizedError(response)) {
    throw new Error("Session expirée. Veuillez vous reconnecter.");
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Erreur HTTP: ${response.status} - ${errorText}`);
  }

  return await response.json();
};

export const createCashFlowSyncRule = async (
  payload: Omit<CashFlowSyncRule, "_id" | "createdAt" | "updatedAt">
): Promise<CashFlowSyncRule> => {
  const response = await fetch(`${API_BASE_URL}/finance/cashflow/sync-rules`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });

  if (handleUnauthorizedError(response)) {
    throw new Error("Session expirée. Veuillez vous reconnecter.");
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Erreur HTTP: ${response.status} - ${errorText}`);
  }

  return await response.json();
};

export const updateCashFlowSyncRule = async (
  id: string,
  payload: Partial<CashFlowSyncRule>
): Promise<CashFlowSyncRule> => {
  const response = await fetch(`${API_BASE_URL}/finance/cashflow/sync-rules/${id}`, {
    method: "PUT",
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });

  if (handleUnauthorizedError(response)) {
    throw new Error("Session expirée. Veuillez vous reconnecter.");
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Erreur HTTP: ${response.status} - ${errorText}`);
  }

  return await response.json();
};

export const deleteCashFlowSyncRule = async (id: string): Promise<{ message: string }> => {
  const response = await fetch(`${API_BASE_URL}/finance/cashflow/sync-rules/${id}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });

  if (handleUnauthorizedError(response)) {
    throw new Error("Session expirée. Veuillez vous reconnecter.");
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Erreur HTTP: ${response.status} - ${errorText}`);
  }

  return await response.json();
};

export const previewCashFlowSyncRule = async (
  id: string,
  limit = 50
): Promise<CashFlowSyncPreviewResponse> => {
  const url = new URL(`${API_BASE_URL}/finance/cashflow/sync-rules/${id}/preview`, window.location.origin);
  url.searchParams.set("limit", String(limit));

  const response = await fetch(url.toString(), {
    headers: getAuthHeaders(),
  });

  if (handleUnauthorizedError(response)) {
    throw new Error("Session expirée. Veuillez vous reconnecter.");
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Erreur HTTP: ${response.status} - ${errorText}`);
  }

  return await response.json();
};

export const applyCashFlowSyncRule = async (
  id: string,
  selectedHashes: string[]
): Promise<CashFlowSyncApplyResponse> => {
  const response = await fetch(`${API_BASE_URL}/finance/cashflow/sync-rules/${id}/apply`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ selectedHashes }),
  });

  if (handleUnauthorizedError(response)) {
    throw new Error("Session expirée. Veuillez vous reconnecter.");
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Erreur HTTP: ${response.status} - ${errorText}`);
  }

  return await response.json();
};

// Fonction pour générer les en-têtes avec le token d'authentification
const getAuthHeaders = (): HeadersInit => {
  const token = getAuthToken();
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  return headers;
};

// Fonction pour convertir les données du formulaire vers le format API
// Sécurisée pour gérer les objets partiels (ex: uniquement dateFin pour archivage)
const formatCashFlowDataForApi = (
  formData: Partial<CashFlowFormData>
): Partial<CashFlowItem> => {
  // Créer une copie basique sans les dates (pour éviter les problèmes de typage)
  const { dateDebut, dateFin, echeance, ...rest } = formData;

  // Construire l'objet avec les conversions de type appropriées
  const apiData: Partial<CashFlowItem> = {
    ...rest
  };
  
  // Vérifier si dateDebut existe et est un objet dayjs avant d'appeler format
  if (dateDebut && typeof dateDebut === 'object' && typeof dateDebut.format === 'function') {
    apiData.dateDebut = dateDebut.format("YYYY-MM-DD");
  } else if (dateDebut) {
    // Si c'est déjà une string ou un autre format, le garder tel quel
    apiData.dateDebut = dateDebut as any;
  }
  
  // Vérifier si dateFin existe et est un objet dayjs avant d'appeler format
  if (dateFin && typeof dateFin === 'object' && typeof dateFin.format === 'function') {
    apiData.dateFin = dateFin.format("YYYY-MM-DD");
  } else if (dateFin) {
    // Si c'est déjà une string ou un autre format, le garder tel quel
    apiData.dateFin = dateFin as any;
  }
  
  // Format de l'échéance selon le type d'opération (uniquement si type est fourni)
  if (formData.type === CashFlowItemType.PONCTUEL && echeance) {
    if (typeof echeance === 'object' && typeof echeance.format === 'function') {
      apiData.echeance = echeance.format("YYYY-MM-DD");
    } else {
      apiData.echeance = echeance as any;
    }
  }

  return apiData;
};

/**
 * Obtenir toutes les opérations financières avec filtres optionnels
 * @param filters Filtres à appliquer (optionnel)
 * @returns Liste des opérations financières
 */
export const getAllCashFlowItems = async (
  filters?: CashFlowFilters
): Promise<CashFlowItem[]> => {
  try {
    // Construire l'URL avec les paramètres de requête si des filtres sont fournis
    let url = `${API_BASE_URL}/finance/cashflow`;

    if (filters) {
      const queryParams = new URLSearchParams();
      if (filters.category) queryParams.append("category", filters.category);
      if (filters.isRevenue !== undefined)
        queryParams.append("isRevenue", filters.isRevenue.toString());
      if (filters.type) queryParams.append("type", filters.type);
      if (filters.startDate) queryParams.append("startDate", filters.startDate);
      if (filters.endDate) queryParams.append("endDate", filters.endDate);
      if (filters.minAmount !== undefined)
        queryParams.append("minAmount", filters.minAmount.toString());
      if (filters.maxAmount !== undefined)
        queryParams.append("maxAmount", filters.maxAmount.toString());

      const queryString = queryParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    const response = await fetch(url, {
      headers: getAuthHeaders(),
    });

    if (response.status === 401) {
      handleUnauthorizedError(response);
      throw new Error("Session expirée ou non autorisée");
    }

    if (!response.ok) {
      throw new Error(`Erreur HTTP: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(
      "Erreur lors de la récupération des opérations financières:",
      error
    );
    throw error;
  }
};

/**
 * Récupérer l'historique des opérations de cash flow
 * @param sinceDays Nombre de jours à remonter (par défaut 10)
 */
export const getCashFlowHistory = async (
  sinceDays = 10
): Promise<CashFlowHistoryEventApi[]> => {
  try {
    const queryParams = new URLSearchParams();
    if (sinceDays) queryParams.append("sinceDays", sinceDays.toString());

    const url = `${API_BASE_URL}/finance/cashflow/history?${queryParams.toString()}`;

    const response = await fetch(url, {
      headers: getAuthHeaders(),
    });

    if (response.status === 401) {
      handleUnauthorizedError(response);
      throw new Error("Session expirée ou non autorisée");
    }

    if (!response.ok) {
      throw new Error(`Erreur HTTP: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(
      "Erreur lors de la récupération de l'historique de cash flow:",
      error
    );
    throw error;
  }
};

/**
 * Enregistrer un événement d'historique de cash flow
 */
export const logCashFlowHistoryEvent = async (
  payload: CreateCashFlowHistoryPayload
): Promise<CashFlowHistoryEventApi> => {
  try {
    const response = await fetch(`${API_BASE_URL}/finance/cashflow/history`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    if (response.status === 401) {
      handleUnauthorizedError(response);
      throw new Error("Session expirée ou non autorisée");
    }

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.message || `Erreur HTTP: ${response.status}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error(
      "Erreur lors de l'enregistrement de l'historique de cash flow:",
      error
    );
    throw error;
  }
};

/**
 * Obtenir une opération financière par son ID
 * @param id ID de l'opération
 * @returns L'opération financière trouvée
 */
export const getCashFlowItemById = async (
  id: string
): Promise<CashFlowItem> => {
  try {
    const response = await fetch(`${API_BASE_URL}/finance/cashflow/${id}`, {
      headers: getAuthHeaders(),
    });

    if (response.status === 401) {
      handleUnauthorizedError(response);
      throw new Error("Session expirée ou non autorisée");
    }

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error("Opération financière non trouvée");
      }
      throw new Error(`Erreur HTTP: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(
      `Erreur lors de la récupération de l'opération financière ${id}:`,
      error
    );
    throw error;
  }
};

/**
 * Créer une nouvelle opération financière
 * @param cashFlowData Données de l'opération financière à créer
 * @returns L'opération financière créée
 */
export const createCashFlowItem = async (
  cashFlowData: CashFlowFormData
): Promise<CashFlowItem> => {
  try {
    const formattedData = formatCashFlowDataForApi(cashFlowData);

    const response = await fetch(`${API_BASE_URL}/finance/cashflow`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(formattedData),
    });

    if (response.status === 401) {
      handleUnauthorizedError(response);
      throw new Error("Session expirée ou non autorisée");
    }

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `Erreur HTTP: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(
      "Erreur lors de la création de l'opération financière:",
      error
    );
    throw error;
  }
};

/**
 * Mettre à jour une opération financière existante
 * @param id ID de l'opération à mettre à jour
 * @param cashFlowData Données à mettre à jour
 * @returns L'opération financière mise à jour
 */
export const updateCashFlowItem = async (
  id: string,
  cashFlowData: Partial<CashFlowFormData>
): Promise<CashFlowItem> => {
  try {
    const formattedData = formatCashFlowDataForApi(
      cashFlowData as CashFlowFormData
    );

    const response = await fetch(`${API_BASE_URL}/finance/cashflow/${id}`, {
      method: "PUT",
      headers: getAuthHeaders(),
      body: JSON.stringify(formattedData),
    });

    if (response.status === 401) {
      handleUnauthorizedError(response);
      throw new Error("Session expirée ou non autorisée");
    }

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `Erreur HTTP: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(
      `Erreur lors de la mise à jour de l'opération financière ${id}:`,
      error
    );
    throw error;
  }
};

/**
 * Supprimer une opération financière
 * @param id ID de l'opération à supprimer
 * @returns Message de confirmation
 */
export const deleteCashFlowItem = async (
  id: string
): Promise<{ message: string }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/finance/cashflow/${id}`, {
      method: "DELETE",
      headers: getAuthHeaders(),
    });

    if (response.status === 401) {
      handleUnauthorizedError(response);
      throw new Error("Session expirée ou non autorisée");
    }

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `Erreur HTTP: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(
      `Erreur lors de la suppression de l'opération financière ${id}:`,
      error
    );
    throw error;
  }
};

/**
 * Obtenir la prévision du cash flow entre deux dates
 * @param startDate Date de début (format YYYY-MM-DD)
 * @param endDate Date de fin (format YYYY-MM-DD)
 * @returns Prévision du cash flow
 */
export const getCashFlowForecast = async (
  startDate: string,
  endDate: string
): Promise<CashFlowForecast[]> => {
  try {
    const queryParams = new URLSearchParams({
      startDate,
      endDate,
    });

    const url = `${API_BASE_URL}/finance/cashflow/forecast?${queryParams.toString()}`;

    const response = await fetch(url, {
      headers: getAuthHeaders(),
    });

    if (response.status === 401) {
      handleUnauthorizedError(response);
      throw new Error("Session expirée ou non autorisée");
    }

    if (!response.ok) {
      throw new Error(`Erreur HTTP: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(
      "Erreur lors de la récupération du cash flow prévisionnel:",
      error
    );
    throw error;
  }
};
